const AUTH_STORAGE_KEY = "auth";
const USER_STORAGE_KEY = "user";
const PERMISSIONS_STORAGE_KEY = "permissions";
const ACCESS_TOKEN_TTL_SECONDS = 30 * 24 * 60 * 60;

// Remove resíduos da implementação anterior que usava sessionStorage incorretamente.
const clearLegacySessionStorage = () => {
  [AUTH_STORAGE_KEY, USER_STORAGE_KEY, PERMISSIONS_STORAGE_KEY].forEach((key) => {
    if (sessionStorage.getItem(key) !== null) sessionStorage.removeItem(key);
  });
};

const isValidStoredAuth = (auth) => {
  const expiresIn = Number(auth?.expires_in);
  const maximumDeadline = Date.now() + expiresIn * 1000;

  return auth?.token_type === "Bearer"
    && typeof auth.access_token === "string"
    && auth.access_token.length > 0
    && expiresIn === ACCESS_TOKEN_TTL_SECONDS
    && Number.isFinite(auth.expires_at)
    && auth.expires_at > Date.now()
    && auth.expires_at <= maximumDeadline;
};

const normalizeAuth = (auth) => {
  const expiresIn = Number(auth?.expires_in);

  if (auth?.token_type !== "Bearer"
    || typeof auth.access_token !== "string"
    || auth.access_token.length === 0
    || expiresIn !== ACCESS_TOKEN_TTL_SECONDS) {
    throw new TypeError("invalid_auth_payload");
  }

  return {
    token_type: "Bearer",
    access_token: auth.access_token,
    expires_in: expiresIn,
    expires_at: Date.now() + expiresIn * 1000,
  };
};

export function getAuth() {
  clearLegacySessionStorage();

  try {
    const value = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!value) return null;

    const auth = JSON.parse(value);
    if (isValidStoredAuth(auth)) return auth;
  } catch {
    // Conteúdo inválido é tratado como sessão encerrada.
  }

  clearIdentity();
  return null;
}

export function hasAuth() {
  return Boolean(getAuth()?.access_token);
}

export function setAuth(auth) {
  const normalized = normalizeAuth(auth);
  localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(normalized));
  clearLegacySessionStorage();
  return normalized;
}

export function clearAuth() {
  localStorage.removeItem(AUTH_STORAGE_KEY);
  sessionStorage.removeItem(AUTH_STORAGE_KEY);
}

export function getUser() {
  try {
    const user = JSON.parse(localStorage.getItem(USER_STORAGE_KEY));
    return user && typeof user === "object" && user.id !== undefined ? user : null;
  } catch {
    return null;
  }
}

export function setIdentity(auth, user, permissions) {
  if (!user || typeof user !== "object" || user.id === undefined || !Array.isArray(permissions)) {
    clearIdentity();
    throw new TypeError("invalid_identity_payload");
  }

  const normalizedAuth = normalizeAuth(auth);

  try {
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(normalizedAuth));
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
    localStorage.setItem(PERMISSIONS_STORAGE_KEY, JSON.stringify(permissions));
    clearLegacySessionStorage();
  } catch (error) {
    clearIdentity();
    throw error;
  }
}

export function setUser(user) {
  if (!user || typeof user !== "object" || user.id === undefined) {
    throw new TypeError("invalid_user_payload");
  }
  localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
  sessionStorage.removeItem(USER_STORAGE_KEY);
}

export function clearIdentity() {
  [AUTH_STORAGE_KEY, USER_STORAGE_KEY, PERMISSIONS_STORAGE_KEY].forEach((key) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });
}

export function syncIdentityFromSession(user, permissions) {
  if (!user || typeof user !== "object" || user.id === undefined || !Array.isArray(permissions)) {
    return false;
  }

  try {
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
    localStorage.setItem(PERMISSIONS_STORAGE_KEY, JSON.stringify(permissions));
    clearLegacySessionStorage();
    return true;
  } catch {
    return false;
  }
}
